/**
 * ENG-7996: per-agent TURN-COMPLETION health, as distinct from process liveness.
 *
 * Every health signal an opencode agent had was process-level or dead:
 *
 *   - the heartbeat's `tmuxAlive` is `tmux has-session` — the serve process, not
 *     its ability to answer;
 *   - `last_pane_activity_at` is never written for opencode at all (the probe
 *     stats `pane.log`, the serve writes `opencode-serve.log`) — ENG-8090;
 *   - the synthetic probe is skipped by default on metered/OpenRouter hosts,
 *     where most opencode agents live — ENG-8091;
 *   - `last_known_good_at` is stamped only by a direct-chat reply or `probe_ack`,
 *     never by a Slack or Telegram turn.
 *
 * So an agent whose serve was up but whose turns never completed reported
 * perfectly healthy. That is not hypothetical: ENG-8058's `question`-permission
 * deadlock hung every turn for ~180s and redelivered forever while ALL monitoring
 * stayed green, and an operator only learned about it because a human noticed the
 * agent had gone quiet.
 *
 * The fix needs no new probe and no new inference. Every opencode turn already
 * resolves to an outcome — the bridge distinguishes declined / admitted / replied,
 * and the manager already branches on it at four call sites — and every one of
 * those outcomes was being thrown away. This records them.
 *
 * Deliberately in-memory and log/diagnostics-only, mirroring
 * `persistent-session-stuck-tracker`: a manager restart resets the streak, which
 * is correct, since a restart also respawns the serve.
 */

/**
 * What one inbound turn resolved to.
 *
 * `declined` is NOT a failure: the sender gate deliberately refused the message
 * (wrong sender, peer policy). It says nothing about whether the agent can
 * answer, and counting it would make a correctly-filtering agent look wedged.
 *
 * `admitted` is a fire-and-forget nudge (`awaitReply: false`). Its turn runs in
 * the background and its outcome is never observed here, so it is neither a
 * success nor a failure — recording it only proves the agent was asked to work.
 */
export type TurnOutcome = 'replied' | 'no_reply' | 'declined' | 'admitted' | 'failed';

export interface TurnHealth {
  /** How the most recent observed turn resolved. Null before any turn. */
  lastOutcome: TurnOutcome | null;
  /** When a turn last produced actual reply text. The "it still works" fact. */
  lastRepliedAt: number | null;
  /** When a turn was last attempted, whatever the outcome. */
  lastAttemptAt: number | null;
  /**
   * Consecutive turns that were admitted and then failed to produce a reply.
   * This is the wedge counter: it is what climbs while `tmuxAlive` stays true.
   */
  consecutiveFailures: number;
}

/** Outcomes that mean "the agent was asked to work and did not answer". */
function isFailure(outcome: TurnOutcome): boolean {
  return outcome === 'no_reply' || outcome === 'failed';
}

function emptyHealth(): TurnHealth {
  return { lastOutcome: null, lastRepliedAt: null, lastAttemptAt: null, consecutiveFailures: 0 };
}

/**
 * Default streak at which a wedged agent is worth a log line. Three consecutive
 * unanswered turns is well past coincidence (a single slow turn or one model
 * hiccup does not reach it) while still firing long before a human would notice
 * the silence.
 */
export const DEFAULT_TURN_FAILURE_WARN_THRESHOLD = 3;

export interface RecordResult {
  health: TurnHealth;
  /**
   * True on the tick the streak first crosses the threshold. Debounced by the
   * `warned` set so a persistently wedged agent produces ONE warning per streak
   * rather than one per turn — same contract as the stuck tracker's.
   */
  shouldWarn: boolean;
  /** True on the turn that ends a warned streak, so recovery is visible too. */
  recovered: boolean;
}

export class TurnOutcomeTracker {
  private readonly health = new Map<string, TurnHealth>();
  private readonly warned = new Set<string>();
  private readonly threshold: number;

  constructor(threshold: number = DEFAULT_TURN_FAILURE_WARN_THRESHOLD) {
    // `threshold < 1` alone lets NaN and Infinity through (both compare false),
    // and either one silently disables warning entirely: `consecutiveFailures >=
    // NaN` is never true, and no finite streak reaches Infinity. A monitoring
    // signal that fails silently is the exact defect this file exists to remove,
    // so reject them loudly. (CodeRabbit, PR #3722.)
    if (!Number.isInteger(threshold) || threshold < 1) {
      throw new Error(`turn-outcome threshold must be an integer >= 1 (got ${threshold})`);
    }
    this.threshold = threshold;
  }

  /** Record one observed turn outcome for one agent. */
  record(codeName: string, outcome: TurnOutcome, now: number = Date.now()): RecordResult {
    const current = this.health.get(codeName) ?? emptyHealth();
    const next: TurnHealth = {
      lastOutcome: outcome,
      lastAttemptAt: now,
      lastRepliedAt: outcome === 'replied' ? now : current.lastRepliedAt,
      // Only a real reply clears the streak. A `declined` or a fire-and-forget
      // `admitted` leaves it exactly where it was: neither proves the agent can
      // answer, so neither should be able to mask an ongoing wedge.
      consecutiveFailures: isFailure(outcome)
        ? current.consecutiveFailures + 1
        : outcome === 'replied'
          ? 0
          : current.consecutiveFailures,
    };
    this.health.set(codeName, next);

    if (outcome === 'replied') {
      const wasWarned = this.warned.delete(codeName);
      return { health: next, shouldWarn: false, recovered: wasWarned };
    }

    const crossed =
      next.consecutiveFailures >= this.threshold && !this.warned.has(codeName);
    if (crossed) this.warned.add(codeName);
    return { health: next, shouldWarn: crossed, recovered: false };
  }

  /** Current turn health for an agent, or null if no turn has been observed. */
  get(codeName: string): TurnHealth | null {
    return this.health.get(codeName) ?? null;
  }

  /**
   * Drop all state for an agent. Called when its serve is torn down, so a fresh
   * serve is not born already carrying the dead one's failure streak.
   */
  reset(codeName: string): void {
    this.health.delete(codeName);
    this.warned.delete(codeName);
  }
}
